import { defineComponent as _defineComponent } from 'vue'
import { cloneDeep } from 'lodash';
import { now } from '@/utils/datetime';
import { PropType, ref, reactive, onMounted, computed } from 'vue';

import {
  SdsProduct,
  SdsSection3,
  SdsSection3Material,
} from '@/interfaces/model/general/sds';
import { NewSdsSection3Material } from '@/lib/constructor/model/general/sds';
import SdsSection3MaterialForm from '@/views/dashboard/sdsDrawer/forms/SdsSection3MaterialForm.vue';
import { VForm } from 'vuetify/lib/components';
import { i18n } from '@/plugins/vue-i18n';
import sharedMessages from '@/lib/shared-messages';
import { useParentMaterials } from '@/composables/useParentMaterials';
import { generateTempMaterialId } from '@/utils/uuid';


export default /*#__PURE__*/_defineComponent({
  __name: 'SdsTableSection3Material',
  props: {
  isEditing: {
    type: Boolean,
    default: false,
  },
  section3: {
    type: Object as PropType<SdsSection3>,
    required: true,
  },
  productIndex: {
    type: Number,
    default: 0,
  },
  products: {
    type: Array as PropType<Array<SdsProduct>>,
    default: () => [],
  },
},
  emits: ["update:section3", "update:products"],
  setup(__props, { emit: emits }: { emit: ({
  (e: 'update:section3', data: SdsSection3): void;
  (e: 'update:products', data: Array<SdsProduct>): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props;

const $t = i18n.t.bind(i18n);



const headerSection3 = [
  {
    text: $t(sharedMessages.chemical),
    value: 'materialName',
    sortable: false,
  },
  { text: $t(sharedMessages.casNumber), value: 'casNumber', sortable: false },
  { text: $t(sharedMessages.encsNumber), value: 'encsNumber', sortable: false },
  { text: $t(sharedMessages.ishlNumber), value: 'ishlNumber', sortable: false },
  {
    text: $t(sharedMessages.concentration),
    value: 'concentration',
    sortable: false,
  },
  {
    text: $t(sharedMessages.parentIndicator),
    value: 'hasParent',
    sortable: false,
    width: '80px',
  },
  {
    text: $t(sharedMessages.parentMaterial),
    value: 'parentMaterialName',
    sortable: false,
    width: '200px',
  },
  { text: $t(sharedMessages.isStated), value: 'is_stated', sortable: false },
  { text: '', value: 'action', sortable: false },
];

const isMaterialConcentrationSecret = ref<boolean>(false);
const isValidUpdateForm = ref<boolean>(false);
const isValidAddForm = ref<boolean>(false);
const form = ref<InstanceType<typeof VForm> | null>(null);
const section3Index = ref<number>(0);
const modalAddSection3Material = ref<boolean>(false);
const modalRescueSection3 = ref<boolean>(false);
const modalDeleteSection3 = ref<boolean>(false);
const modalEditSection3Material = ref<boolean>(false);
const newMaterial = reactive<SdsSection3Material>({
  ...cloneDeep(NewSdsSection3Material),
  sds_id: props.section3.sds_id,
  sds_section3_id: props.section3.id,
  concentration_unit: '%',
});
const updatedMaterial = reactive<SdsSection3Material>({
  ...NewSdsSection3Material,
});

// Parent materials composable integration
const materialsRef = computed(() => props.section3.material_list);
const { enrichedMaterials } = useParentMaterials(materialsRef);

// Computed properties for type conversion (null <-> undefined)
const newMaterialConcentrationMin = computed({
  get: () => newMaterial.concentration_min ?? undefined,
  set: (val) => {
    newMaterial.concentration_min = val ?? null;
  },
});

const newMaterialConcentrationMax = computed({
  get: () => newMaterial.concentration_max ?? undefined,
  set: (val) => {
    newMaterial.concentration_max = val ?? null;
  },
});

const newMaterialParentId = computed({
  get: () => newMaterial.parent_material_id ?? undefined,
  set: (val) => {
    newMaterial.parent_material_id = val ?? null;
  },
});

const updatedMaterialConcentrationMin = computed({
  get: () => updatedMaterial.concentration_min ?? undefined,
  set: (val) => {
    updatedMaterial.concentration_min = val ?? null;
  },
});

const updatedMaterialConcentrationMax = computed({
  get: () => updatedMaterial.concentration_max ?? undefined,
  set: (val) => {
    updatedMaterial.concentration_max = val ?? null;
  },
});

const updatedMaterialParentId = computed({
  get: () => updatedMaterial.parent_material_id ?? undefined,
  set: (val) => {
    updatedMaterial.parent_material_id = val ?? null;
  },
});

const openAddSection3MaterialModal = (): void => {
  modalAddSection3Material.value = true;
};

const closeAddSection3MaterialModal = (): void => {
  modalAddSection3Material.value = false;
};

const openDeleteSection3Modal = (): void => {
  modalDeleteSection3.value = true;
};

const closeDeleteSection3Modal = (): void => {
  modalDeleteSection3.value = false;
};

const openRescueSection3Modal = (): void => {
  modalRescueSection3.value = true;
};

const closeRescueSection3Modal = (): void => {
  modalRescueSection3.value = false;
};

const openEditSection3MaterialModal = (): void => {
  modalEditSection3Material.value = true;
};

const closeEditSection3MaterialModal = (): void => {
  modalEditSection3Material.value = false;
};



const bindUpdatedMaterial = (material: SdsSection3Material): void => {
  updatedMaterial.id = material.id;
  updatedMaterial.sds_id = material.sds_id;
  updatedMaterial.sds_section3_id = material.sds_section3_id;
  updatedMaterial.material_name = material.material_name;
  updatedMaterial.concentration = material.concentration;
  updatedMaterial.concentration_inequality_sign =
    material.concentration_inequality_sign;
  updatedMaterial.concentration_min = material.concentration_min;
  updatedMaterial.concentration_max = material.concentration_max;
  updatedMaterial.concentration_unit = material.concentration_unit;
  updatedMaterial.encs_number = material.encs_number;
  updatedMaterial.ishl_number = material.ishl_number;
  updatedMaterial.is_stated = material.is_stated;
  updatedMaterial.cas_list = material.cas_list;
  updatedMaterial.deleted_at = material.deleted_at;

  // Bind parent material fields
  updatedMaterial.parent_material_id = material.parent_material_id;
  updatedMaterial.temp_material_id = material.temp_material_id;
  updatedMaterial.temp_parent_material_id = material.temp_parent_material_id;

  isMaterialConcentrationSecret.value =
    material.concentration_min == null && material.concentration_max == null;
};

const concentrationText = (material: SdsSection3Material): string => {
  let text = '';

  switch (material.concentration_inequality_sign) {
    case 0:
      text = String($t(sharedMessages.nonenPublic));
      break;
    case 1:
      text =
        material.concentration_min +
        material.concentration_unit +
        String($t(sharedMessages.equal));
      break;
    case 2:
      text =
        material.concentration_min +
        material.concentration_unit +
        String($t(sharedMessages.greaterThanOrEqual));
      break;
    case 3:
      text =
        material.concentration_min +
        material.concentration_unit +
        String($t(sharedMessages.greaterThan));
      break;
    case 4:
      text =
        material.concentration_max +
        material.concentration_unit +
        String($t(sharedMessages.lessThanOrEqual));
      break;
    case 5:
      text =
        material.concentration_max +
        material.concentration_unit +
        String($t(sharedMessages.lessThan));
      break;
    case 6:
      text =
        material.concentration_min +
        material.concentration_unit +
        String($t(sharedMessages.greaterThanOrEqual)) +
        material.concentration_max +
        material.concentration_unit +
        String($t(sharedMessages.lessThanOrEqual));
      break;
    case 7:
      text =
        material.concentration_min +
        material.concentration_unit +
        String($t(sharedMessages.greaterThanOrEqual)) +
        material.concentration_max +
        material.concentration_unit +
        String($t(sharedMessages.lessThan));
      break;
    case 8:
      text =
        material.concentration_min +
        material.concentration_unit +
        String($t(sharedMessages.greaterThan)) +
        material.concentration_max +
        material.concentration_unit +
        String($t(sharedMessages.lessThanOrEqual));
      break;
    case 9:
      text =
        material.concentration_min +
        material.concentration_unit +
        String($t(sharedMessages.greaterThan)) +
        material.concentration_max +
        material.concentration_unit +
        String($t(sharedMessages.lessThan));
      break;
    default:
      text = String($t(sharedMessages.nonenPublic));
      break;
  }

  return text;
};

const clearNewMaterial = (): void => {
  newMaterial.material_name = '';
  newMaterial.concentration = '';
  newMaterial.concentration_inequality_sign = 0;
  newMaterial.concentration_min = 0;
  newMaterial.concentration_max = 0;
  newMaterial.concentration_unit = '%';
  newMaterial.encs_number = '';
  newMaterial.ishl_number = '';
  newMaterial.is_stated = false;
  newMaterial.cas_list = [];
  newMaterial.deleted_at = null;

  // Clear and regenerate parent material fields
  newMaterial.parent_material_id = null;
  newMaterial.temp_material_id = generateTempMaterialId();
  newMaterial.temp_parent_material_id = undefined;
  newMaterial.hasParent = false;
  newMaterial.parentMaterialName = '';
  newMaterial.isParentDeleted = false;
};

const updateMaterial = (material: SdsSection3Material, index: number): void => {
  const section3 = { ...props.section3 };
  section3.material_list[index].id = material.id;
  section3.material_list[index].sds_id = material.sds_id;
  section3.material_list[index].sds_section3_id = material.sds_section3_id;
  section3.material_list[index].material_name = material.material_name;
  section3.material_list[index].concentration = material.concentration;
  section3.material_list[index].concentration_inequality_sign =
    material.concentration_inequality_sign;
  section3.material_list[index].concentration_min = material.concentration_min;
  section3.material_list[index].concentration_max = material.concentration_max;
  section3.material_list[index].concentration_unit =
    material.concentration_unit;
  section3.material_list[index].encs_number = material.encs_number;
  section3.material_list[index].ishl_number = material.ishl_number;
  section3.material_list[index].cas_list = material.cas_list;
  section3.material_list[index].is_stated = material.is_stated;
  section3.material_list[index].deleted_at = material.deleted_at;

  // Update parent material fields
  section3.material_list[index].parent_material_id =
    material.parent_material_id;
  section3.material_list[index].temp_material_id = material.temp_material_id;
  section3.material_list[index].temp_parent_material_id =
    material.temp_parent_material_id;

  emits('update:section3', section3);
};

const rescueSection3 = (): void => {
  const data = props.section3;
  data.material_list[section3Index.value].deleted_at = null;
  for (const cas of data.material_list[section3Index.value].cas_list) {
    cas.deleted_at = null;
  }

  emits('update:section3', data);
};

const deleteSection3 = (): void => {
  const data = props.section3;
  data.material_list[section3Index.value].deleted_at = now().toString();
  for (const cas of data.material_list[section3Index.value].cas_list) {
    cas.deleted_at = now().toString();
  }

  emits('update:section3', data);
};

const addMaterial = (): void => {
  // Ensure new material has a temp_material_id before adding
  if (!newMaterial.temp_material_id) {
    newMaterial.temp_material_id = generateTempMaterialId();
  }

  const updatedData = structuredClone(props.section3);
  switch (newMaterial.concentration_inequality_sign) {
    case 0:
      newMaterial.concentration_min = null;
      newMaterial.concentration_max = null;
      break;
    case 1:
      newMaterial.concentration_max = newMaterial.concentration_min;
      break;
    case 2:
      newMaterial.concentration_max = 100;
      break;
    case 3:
      newMaterial.concentration_max = 100;
      break;
    case 4:
      newMaterial.concentration_min = 0;
      break;
    case 5:
      newMaterial.concentration_min = 0;
      break;
    case 6:
      break;
    case 7:
      break;
    case 8:
      break;
    case 9:
      break;
    default:
      break;
  }

  updatedData.material_list.push(structuredClone(newMaterial));
  emits('update:section3', updatedData);
};

onMounted((): void => {
  if (form.value && 'validate' in form.value) {
    // VForm type doesn't expose validate method, but it exists at runtime
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (form.value as any).validate();
  }
});

return { __sfc: true,$t, props, headerSection3, isMaterialConcentrationSecret, isValidUpdateForm, isValidAddForm, form, section3Index, modalAddSection3Material, modalRescueSection3, modalDeleteSection3, modalEditSection3Material, newMaterial, updatedMaterial, materialsRef, enrichedMaterials, newMaterialConcentrationMin, newMaterialConcentrationMax, newMaterialParentId, updatedMaterialConcentrationMin, updatedMaterialConcentrationMax, updatedMaterialParentId, openAddSection3MaterialModal, closeAddSection3MaterialModal, openDeleteSection3Modal, closeDeleteSection3Modal, openRescueSection3Modal, closeRescueSection3Modal, openEditSection3MaterialModal, closeEditSection3MaterialModal, emits, bindUpdatedMaterial, concentrationText, clearNewMaterial, updateMaterial, rescueSection3, deleteSection3, addMaterial, SdsSection3MaterialForm, sharedMessages }
}

})